/**
 * Feb 2026 R293zh · Standalone-Estimate picker.
 *
 * Opens as a modal from either:
 *   • Composer's "Import from Estimate" button (seeds lines + customer)
 *   • GstSales list top toolbar "From Estimate" (one-click conversion)
 *
 * In "import" mode (default) we fetch /lines and call onPick(payload)
 * so the parent composer can seed its state. In "convert" mode we
 * POST /to-gst-quotation and call onConverted(quotation).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, FileCheck2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function EstimatePicker({ open, onClose, ws, mode = "import", onPick, onConverted }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("approved");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || !ws) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/standalone-estimates`, {
          params: { status, q: q || undefined },
        });
        if (!cancelled) setItems(r.data.items || []);
      } catch (e) { toast.error(apiErrorMessage(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, ws, status, q]);

  const handleChoose = async (row) => {
    setBusyId(row.id);
    try {
      if (mode === "convert") {
        const r = await api.post(
          `/gst/workspaces/${ws}/standalone-estimates/${row.id}/to-gst-quotation`,
          {},
        );
        toast.success(r.data?.reused
          ? `Opened existing quotation from ${row.number}`
          : `Created quotation from ${row.number}`);
        onConverted?.(r.data.quotation);
        onClose?.();
      } else {
        const r = await api.get(`/gst/workspaces/${ws}/standalone-estimates/${row.id}/lines`);
        onPick?.({ ...r.data, _row: row });
        onClose?.();
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="estimate-picker"
        className="w-screen max-w-full h-[100dvh] max-h-[100dvh] p-0 gap-0 rounded-none border-0 flex flex-col
                     sm:max-w-[min(900px,96vw)] sm:h-auto sm:max-h-[90vh] sm:rounded-xl"
      >
        <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="h-4 w-4 text-emerald-600" />
            {mode === "convert" ? "Convert Estimate → Quotation" : "Import from Estimate"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === "convert"
              ? "One-click: creates a GST quotation (QUO-) from an approved cost sheet."
              : "Pick an approved estimate — we'll seed the line items and customer into this composer."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 py-2 border-b border-border flex items-center gap-2 shrink-0">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by number or title"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-9"
              data-testid="estimate-picker-search"
            />
          </div>
          <div className="flex items-center gap-1">
            {["approved", "all"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`text-[11px] uppercase px-2 py-1 rounded border transition-colors ${
                  status === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
                data-testid={`estimate-picker-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0 bg-muted/10">
          {loading ? (
            <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div>
          ) : !items.length ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground/40 mb-2" />
              <div className="text-sm text-muted-foreground">No matching estimates</div>
              <div className="text-xs text-muted-foreground mt-1">
                {status === "approved" && "Only approved estimates are eligible for conversion."}
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {items.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  disabled={busyId === e.id}
                  onClick={() => handleChoose(e)}
                  className="w-full text-left border border-border rounded px-3 py-2 bg-background hover:bg-muted transition-colors flex items-center justify-between gap-3"
                  data-testid={`estimate-picker-row-${e.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-semibold">{e.number || "—"}</span>
                      {e.version > 1 && (
                        <span className="text-[9px] font-mono px-1 border border-violet-300 dark:border-violet-800 rounded text-violet-700 dark:text-violet-200">
                          v{e.version}
                        </span>
                      )}
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${
                        e.status === "approved" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200" :
                        e.status === "draft" ? "bg-muted text-muted-foreground" :
                        e.status === "converted" ? "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-200" :
                        "bg-neutral-200 text-neutral-600"
                      }`}>
                        {e.status}
                      </span>
                    </div>
                    <div className="text-sm text-foreground truncate mt-0.5">{e.title || "Untitled estimate"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-mono tabular-nums">
                      {Number(e.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })} {e.currency || "INR"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{(e.created_at || "").slice(0, 10)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex justify-end shrink-0">
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
